import { useContext, useMemo } from 'react';
import { GEN_YEAR_OPTIONS } from 'components/Organisms/FormHouseholdVehicle/constants';
import { useIntl } from 'react-intl';
import { AppContext } from 'contexts/AppContext';
import useGetVehicleModelsQuery from 'hooks/queries/useGetVehicleModelsQuery';

const years = GEN_YEAR_OPTIONS();

export const GridProps = {
  xs: 12,
  sm: 6,
};

export const VEHICLE_FIELD_NAMES = {
  type: 'type',
  year: 'year',
  make: 'make',
  model: 'model',
  color: 'color',
  licensePlateNumber: 'licensePlateNumber',
  state: 'state',
  isOpen: 'isOpen',
};

export const NEW_VEHICLE = {
  [VEHICLE_FIELD_NAMES.type]: '',
  [VEHICLE_FIELD_NAMES.year]: '',
  [VEHICLE_FIELD_NAMES.make]: '',
  [VEHICLE_FIELD_NAMES.model]: '',
  [VEHICLE_FIELD_NAMES.color]: '',
  [VEHICLE_FIELD_NAMES.licensePlateNumber]: '',
  [VEHICLE_FIELD_NAMES.state]: '',
  [VEHICLE_FIELD_NAMES.isOpen]: true,
};

const useVehicleFields = ({ formId, watch }) => {
  const intl = useIntl();
  const {
    states,
    vehicleOptions: { colors, makes, types },
  } = useContext(AppContext);
  const makeId = watch(VEHICLE_FIELD_NAMES.make);
  const { models } = useGetVehicleModelsQuery({
    makeId,
  });

  const options = useMemo(() => {
    return {
      colors: colors?.map((color) => ({
        text: color.name,
        value: color.id,
      })),
      types: types?.map((type) => ({
        text: type.name,
        value: type.id,
      })),
      years: years?.map((year) => ({
        text: year.label,
        value: year.value.toString(),
      })),
      makes: makes?.map((make) => ({
        text: make.name,
        value: make.id,
      })),
      models: models?.map((model) => ({
        text: model.name,
        value: model.id,
      })),
      states: states?.map((state) => ({
        text: state.name,
        value: state.id,
      })),
    };
  }, [colors, makes, models, states, types]);

  const vehicleFields = useMemo(
    () =>
      [
        {
          name: VEHICLE_FIELD_NAMES.type,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.type',
          }),
          variant: 'singleSelect',
          options: options.types,
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.year,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.year',
          }),
          variant: 'singleSelect',
          options: options.years,
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.make,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.make',
          }),
          variant: 'singleSelect',
          options: options.makes,
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.model,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.model',
          }),
          variant: 'singleSelect',
          options: options.models,
          disabled: !options.models.length > 0,
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.color,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.color',
          }),
          variant: 'singleSelect',
          options: options.colors,
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.licensePlateNumber,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.licensePlateNumber',
          }),
          variant: 'text',
          placeholder: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.licensePlateNumber',
          }),
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: VEHICLE_FIELD_NAMES.state,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.vehicle.state',
          }),
          variant: 'singleSelect',
          options: options.states,
          rules: {
            required: true,
          },
          GridProps,
        },
      ].map((field) => ({
        ...field,
        id: formId,
      })),
    [
      formId,
      intl,
      options.colors,
      options.makes,
      options.models,
      options.states,
      options.types,
      options.years,
    ],
  );

  return {
    vehicleFields,
    makes: options.makes,
    models: options.models,
  };
};

export default useVehicleFields;
